import { createPortal } from "react-dom";
import "./scanner.css";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

/**
 * BarcodeScannerModal — Overlay scanner (native detector when available, ZXing fallback)
 *
 * Props:
 *   - onClose(): void
 *   - onDetected(code: string): void
 */
export default function BarcodeScannerModal({ onClose, onDetected }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null); // ZXing controls
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const detectorRef = useRef(null);

  const [usingNative, setUsingNative] = useState(false);
  const [error, setError] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => sessionStorage.getItem("bcam") || "");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [loading, setLoading] = useState(true);

  const modalId = useId();
  const isDetectorSupported = useMemo(
    () => typeof window !== "undefined" && "BarcodeDetector" in window,
    []
  );

  // ---------- Helpers ----------
  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  };

  const stopStream = () => {
    try {
      const tracks = streamRef.current?.getTracks?.() || [];
      tracks.forEach((t) => t.stop());
    } catch {}
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      try { videoRef.current.srcObject = null; } catch {}
    }
    streamRef.current = null;
  };

  const stopAll = () => {
    stopRaf();
    try { controlsRef.current?.stop?.(); } catch {}
    try { readerRef.current?.reset?.(); } catch {}
    detectorRef.current = null;
    stopStream();
    setTorchOn(false);
  };

  const applyTorch = async (on) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) return;
      const capabilities = track.getCapabilities?.() || {};
      if (!capabilities.torch) return;
      await track.applyConstraints({ advanced: [{ torch: on }] });
      setTorchOn(on);
    } catch {}
  };

  const checkTorchSupport = () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      const caps = track?.getCapabilities?.();
      setTorchSupported(!!caps?.torch);
    } catch {
      setTorchSupported(false);
    }
  };

  // ---------- Core start ----------
  const start = async (preferredDeviceId = "") => {
    setLoading(true);
    setError("");
    stopAll();

    try {
      // discover devices first
      const all = (await navigator.mediaDevices.enumerateDevices()) || [];
      const cams = all.filter((d) => d.kind === "videoinput");
      setDevices(cams);

      const constraints = {
        audio: false,
        video: preferredDeviceId
          ? { deviceId: { exact: preferredDeviceId }, focusMode: "continuous", facingMode: { ideal: "environment" } }
          : { facingMode: { ideal: "environment" }, focusMode: "continuous" },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      checkTorchSupport();

      if (isDetectorSupported) {
        setUsingNative(true);
        detectorRef.current = new window.BarcodeDetector();
        const loop = async () => {
          if (!videoRef.current || !detectorRef.current) return;
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes && codes.length) {
              onDetected?.(codes[0].rawValue);
              return;
            }
          } catch {}
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setUsingNative(false);
        readerRef.current = new BrowserMultiFormatReader();
        controlsRef.current = await readerRef.current.decodeFromVideoDevice(
          preferredDeviceId || undefined,
          videoRef.current,
          (result) => {
            if (result) onDetected?.(result.getText());
          }
        );
      }
    } catch (e) {
      console.warn(e);
      setError(
        "Cannot access camera. Use HTTPS/localhost, allow permission, and ensure no other app is using the camera."
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------- Effects ----------
  useEffect(() => {
    // lock background scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        stopAll();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    const vis = () => {
      if (document.visibilityState === "hidden") stopAll();
    };
    document.addEventListener("visibilitychange", vis);

    start(deviceId);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("visibilitychange", vis);
      document.body.style.overflow = prevOverflow;
      stopAll();
    };
  }, [deviceId, onClose]);

  // Persist chosen camera
  useEffect(() => {
    try {
      if (deviceId) sessionStorage.setItem("bcam", deviceId);
    } catch {}
  }, [deviceId]);

  const handleClose = () => {
    stopAll();
    onClose?.();
  };

  // ---------- UI ----------
  return createPortal(
    <div
      className="scanner-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${modalId}-title`}
    >
      <div className="scanner-backdrop" onClick={handleClose} />

      <div className="scanner-box">
        {/* Header separated with explicit bottom margin & extra right padding */}
        <div
          className="card-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: 8,
            marginBottom: 16,           // <-- ensures gap from action buttons below
            borderBottom: "1px solid rgba(0,0,0,0.06)"
          }}
        >
          <strong id={`${modalId}-title`}>Scan Barcode</strong>

          {/* Icon-size close button so it doesn’t crowd nearby controls */}
          <button
            className="btn"
            onClick={handleClose}
            aria-label="Close scanner"
            title="Close"
            style={{
              width: 36,
              height: 36,
              lineHeight: "36px",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: 8,
              borderRadius: 6
            }}
          >
            ✕
          </button>
        </div>

        <div className="card-body" style={{ display: "grid", gap: 12 }}>
          {/* Device & Actions Row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              alignItems: "center"
            }}
          >
            <div>
              {devices.length > 1 && (
                <select
                  className="input"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  title="Choose camera"
                >
                  <option value="">Default camera</option>
                  {devices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* This block sits below the header with its own spacing,
                and right-aligned; it no longer visually collides with ✕ */}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => start(deviceId)} disabled={loading} title="Restart">
                {loading ? "Starting…" : "Restart"}
              </button>
              <button
                className="btn"
                onClick={() => applyTorch(!torchOn)}
                disabled={!torchSupported}
                title={torchSupported ? (torchOn ? "Turn torch off" : "Turn torch on") : "Torch not supported"}
              >
                {torchSupported ? (torchOn ? "Torch On" : "Torch Off") : "No Torch"}
              </button>
            </div>
          </div>

          <video
            ref={videoRef}
            className="w-full rounded"
            muted
            playsInline
            style={{ width: "100%", borderRadius: 8, background: "#000" }}
          />

          <div className="muted" style={{ fontSize: 12 }}>
            {usingNative ? "Using native scanner." : "Using ZXing fallback."}
          </div>

          {error && (
            <div style={{ color: "#b00020", fontSize: 12 }} role="alert">
              {error}
            </div>
          )}

          <div className="muted" style={{ fontSize: 12 }}>
            Tips: ensure good lighting and hold the code 15–25 cm from the camera for faster detection.
          </div>
        </div>

        <div className="card-footer" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn" onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
